import { supabase, isSupabaseEnabled } from './supabase'
import { getLocalBalance } from './stripe'
import { submitMemberDivisionPicks } from './odds'
import { log } from './log'

// ── localStorage helpers (fallback when Supabase is not configured) ───────────
const KEYS = { users:'wb_users', profile:'wb_profile', groups:'wb_groups', members:'wb_members' }

function read(k)      { try { return JSON.parse(localStorage.getItem(k) || 'null') } catch { return null } }
function write(k, v)  { localStorage.setItem(k, JSON.stringify(v)) }
function uid()        { return Math.random().toString(36).slice(2) + Date.now().toString(36) }
function now()        { return new Date().toISOString() }
export function makeCode()   { const c='ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; return Array.from({length:6},()=>c[Math.floor(Math.random()*c.length)]).join('') }

// ── Day key (YYYY-MM-DD) ──────────────────────────────────────────────────────
// Returns today's date in YYYY-MM-DD format using the LOCAL timezone.
// Used for client-side "submitted today" lockout (PickemMode.jsx) and as
// the default for create-form start_date (App.jsx). The previous UTC-based
// implementation pre-filled tomorrow's date after ~8 PM EDT and held the
// submit lock past local midnight — both confusing for users. See
// .claude/getdaykey-audit.md for the full call-site map and Scenario C-1
// transition note.
export function getDayKey() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ── Group lifecycle dates ─────────────────────────────────────────────────────
// Computes start/end dates for a group based on its duration type. Used at
// creation time so we can later split the group list into active vs historical.
// durationType: 'daily' | 'weekly' | 'season' | 'playoffs'
export function calculateGroupDates(durationType, sport, startDate) {
  const start = startDate ? new Date(startDate) : new Date()
  let end = new Date(start)
  const type = (durationType || '').toLowerCase()

  if (type === 'daily') {
    // end stays equal to start — daily pool spans exactly the start
    // day. Both deriveContestClass (src/lib/stripe.js) and the server-
    // side migration 036 compute pool_days = (end - start) + 1 inclusive,
    // so end=start → pool_days=1 → daily_pickem ($2.99). Previously
    // end = start + 1 gave pool_days = 2 → weekly_pickem ($7.99).
  } else if (type === 'weekly') {
    // End on the upcoming Sunday — getDay() returns 0 for Sunday, so wrap.
    const daysUntilSunday = (7 - end.getDay()) % 7 || 7
    end.setDate(end.getDate() + daysUntilSunday)
  } else if (type === 'season') {
    end.setMonth(end.getMonth() + 6)
  } else if (type === 'playoffs') {
    const y = start.getFullYear()
    const playoffEnds = {
      NBA: new Date(y, 5, 30),  // Jun 30
      NHL: new Date(y, 5, 30),
      MLB: new Date(y, 10, 30), // Nov 30
      NFL: new Date(y, 1, 28),  // Feb 28 of same year window
    }
    end = playoffEnds[sport] || end
  } else {
    end.setDate(end.getDate() + 7)
  }

  return { start, end }
}

// ── Friendly error messages ───────────────────────────────────────────────────
function friendly(err) {
  const msg = (err?.message || err?.error_description || '').toLowerCase()
  if (msg.includes('invalid login') || msg.includes('invalid credentials')) return 'Incorrect email or password'
  if (msg.includes('already registered') || msg.includes('already exists') || msg.includes('duplicate')) return 'An account with this email already exists'
  if (msg.includes('email not confirmed')) return 'Please confirm your email before signing in'
  if (msg.includes('network') || msg.includes('fetch')) return 'Connection error — check your internet and try again'
  if (msg.includes('jwt') || msg.includes('session')) return 'Session expired — please sign in again'
  return err?.message || 'Something went wrong — please try again'
}

// ── Profiles ──────────────────────────────────────────────────────────────────
// profiles columns: id, username, total_points, created_at, total_won

export async function createProfile(userId, username) {
  if (!isSupabaseEnabled) return
  const { error } = await supabase.from('profiles').insert({
    id:           userId,
    username:     username,
    total_points: 0,
    total_won:    0,
  })
  if (error) log('createProfile error:', error)
}

// Fetch a single profile by id. All fields public per migration 011 RLS.
// Used by the view-other-profile screen.
export async function getProfile(userId) {
  if (!isSupabaseEnabled || !userId) return null
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, avatar_url, total_points, total_won, created_at')
    .eq('id', userId)
    .single()
  if (error) { log('getProfile error:', error); return null }
  return data
}

// ── Follows ───────────────────────────────────────────────────────────────────
// follows columns: follower_id, followee_id, created_at (composite PK).
// RLS: SELECT public, INSERT/DELETE require auth.uid()=follower_id, no UPDATE
// (migration 027). All four helpers below rely on the policies, not client guards.

export async function followUser(targetId) {
  if (!isSupabaseEnabled || !targetId) return { ok: false }
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return { ok: false, error: 'auth required' }
  if (user.id === targetId) return { ok: false, error: 'cannot follow self' }
  const { error } = await supabase
    .from('follows')
    .insert({ follower_id: user.id, followee_id: targetId })
  // Duplicate-follow is a unique-violation (23505); treat as success.
  if (error && error.code !== '23505') {
    log('followUser error:', error)
    return { ok: false, error: error.message }
  }
  return { ok: true }
}

export async function unfollowUser(targetId) {
  if (!isSupabaseEnabled || !targetId) return { ok: false }
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return { ok: false, error: 'auth required' }
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', user.id)
    .eq('followee_id', targetId)
  if (error) { log('unfollowUser error:', error); return { ok: false, error: error.message } }
  return { ok: true }
}

// Users that `userId` follows. Join surfaces username+avatar for display.
export async function getFollowing(userId) {
  if (!isSupabaseEnabled || !userId) return []
  const { data, error } = await supabase
    .from('follows')
    .select('followee_id, created_at, profiles!follows_followee_id_fkey(id, username, avatar_url)')
    .eq('follower_id', userId)
    .order('created_at', { ascending: false })
  if (error) { log('getFollowing error:', error); return [] }
  return (data || []).map(r => ({
    user_id:    r.followee_id,
    created_at: r.created_at,
    username:   r.profiles?.username   || r.followee_id,
    avatar_url: r.profiles?.avatar_url || null,
  }))
}

// Users that follow `userId`. Same join shape as getFollowing.
export async function getFollowers(userId) {
  if (!isSupabaseEnabled || !userId) return []
  const { data, error } = await supabase
    .from('follows')
    .select('follower_id, created_at, profiles!follows_follower_id_fkey(id, username, avatar_url)')
    .eq('followee_id', userId)
    .order('created_at', { ascending: false })
  if (error) { log('getFollowers error:', error); return [] }
  return (data || []).map(r => ({
    user_id:    r.follower_id,
    created_at: r.created_at,
    username:   r.profiles?.username   || r.follower_id,
    avatar_url: r.profiles?.avatar_url || null,
  }))
}

// Returns peer user_ids (with profile fields) that share any group with viewer,
// excluding viewer themself. Source for the "Find people" discovery list.
// Per the project spec, discovery is shared-pool-only — no open username search.
export async function getSharedPoolMemberIds(viewerId) {
  if (!isSupabaseEnabled || !viewerId) return []
  const { data: myRows, error: myErr } = await supabase
    .from('group_members')
    .select('group_id')
    .eq('user_id', viewerId)
  if (myErr) { log('getSharedPoolMemberIds (mine) error:', myErr); return [] }
  const groupIds = (myRows || []).map(r => r.group_id)
  if (groupIds.length === 0) return []
  const { data: peerRows, error: peerErr } = await supabase
    .from('group_members')
    .select('user_id, profiles(username, avatar_url)')
    .in('group_id', groupIds)
    .neq('user_id', viewerId)
  if (peerErr) { log('getSharedPoolMemberIds (peers) error:', peerErr); return [] }
  // De-dupe — a user can appear in multiple shared groups.
  const seen = new Map()
  for (const r of (peerRows || [])) {
    if (seen.has(r.user_id)) continue
    seen.set(r.user_id, {
      user_id:    r.user_id,
      username:   r.profiles?.username   || r.user_id,
      avatar_url: r.profiles?.avatar_url || null,
    })
  }
  return Array.from(seen.values())
}

// Returns groups that BOTH viewer and target are members of. Used by the
// view-other-profile screen to render the shared-pools list.
export async function getSharedPools(viewerId, targetId) {
  if (!isSupabaseEnabled || !viewerId || !targetId) return []
  const [mineRes, theirsRes] = await Promise.all([
    supabase.from('group_members').select('group_id').eq('user_id', viewerId),
    supabase.from('group_members').select('group_id').eq('user_id', targetId),
  ])
  if (mineRes.error)  { log('getSharedPools (mine) error:',  mineRes.error);  return [] }
  if (theirsRes.error){ log('getSharedPools (theirs) error:',theirsRes.error);return [] }
  const mine   = new Set((mineRes.data   || []).map(r => r.group_id))
  const shared = (theirsRes.data || []).map(r => r.group_id).filter(id => mine.has(id))
  if (shared.length === 0) return []
  const { data, error } = await supabase
    .from('groups')
    .select('id, name, sport, contest_type, status, invite_code')
    .in('id', shared)
  if (error) { log('getSharedPools (groups) error:', error); return [] }
  return data || []
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function signUp(email, password, username) {
  if (isSupabaseEnabled) {
    try {
      // emailRedirectTo lands the user back on the app after they click the
      // confirmation email — window.location.origin so prod + previews +
      // local dev all redirect to the host they signed up from. (Whatever
      // origin you use must be on Supabase's allowlist under Authentication
      // → URL Configuration → Redirect URLs.)
      const redirectTo = typeof window !== 'undefined' ? window.location.origin : undefined
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: redirectTo ? { emailRedirectTo: redirectTo } : undefined,
      })
      if (error) throw new Error(friendly(error))
      if (data.user) {
        await createProfile(data.user.id, username)
      }
      return { id: data.user?.id, email, username }
    } catch (err) {
      throw new Error(err.message || 'Sign up failed')
    }
  }
  console.error('[auth] isSupabaseEnabled=false — VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY missing at build time')
  throw new Error('Authentication is not configured. Please contact support.')
}

export async function signIn(email, password) {
  if (isSupabaseEnabled) {
    // Return as soon as auth succeeds. Skipping the profiles fetch here
    // avoids a slow/failed .single() call hanging the Sign In button.
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw new Error(friendly(error))
    return {
      id:       data.user.id,
      email:    data.user.email,
      username: data.user.email.split('@')[0],
    }
  }
  console.error('[auth] isSupabaseEnabled=false — VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY missing at build time')
  throw new Error('Authentication is not configured. Please contact support.')
}

export async function signOut() {
  if (isSupabaseEnabled) {
    const { error } = await supabase.auth.signOut()
    if (error) log('signOut error:', error)
  }
  localStorage.removeItem(KEYS.profile)
}

// In-app account deletion — DELETE /api/account on the Railway backend
// with the user's Bearer token. Server contract (see server/index.js
// DELETE /api/account):
//   200  → user gone, sign out
//   401  → session gone, treat as already-deleted, same sign-out path
//   403  → CANNOT_DELETE_SENTINEL (should never reach a real user)
//   500/503/network → transient, caller shows retry message
// Returns a flat result the modal dispatches on.
export async function deleteAccount() {
  if (!isSupabaseEnabled) return { ok: false, status: 'no-supabase' }
  let token = null
  try {
    const { data } = await supabase.auth.getSession()
    token = data?.session?.access_token || null
  } catch (err) {
    log('[delete-account] session read failed:', err?.message || err)
  }
  if (!token) return { ok: false, status: 'no-session' }

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
  try {
    const res = await fetch(`${API_URL}/api/account`, {
      method:  'DELETE',
      headers: { Authorization: `Bearer ${token}` },
      signal:  AbortSignal.timeout(20_000),
    })
    if (res.ok) return { ok: true, status: res.status }
    return { ok: false, status: res.status }
  } catch (err) {
    return { ok: false, status: 'network', message: err?.message }
  }
}

export async function getCurrentUser() {
  if (isSupabaseEnabled) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) return null
      const { data: prof } = await supabase.from('profiles')
        .select('id, username, total_points, total_won, avatar_url, bypass_payment')
        .eq('id', session.user.id)
        .single()
      return {
        id:           session.user.id,
        email:        session.user.email,
        username:     prof?.username || session.user.email?.split('@')[0],
        avatar_url:   prof?.avatar_url || null,
        // Strict === true coercion (backlog 27, Phase D commit 1). DO NOT
        // "tidy" to !!prof?.bypass_payment or drop the strict check. The
        // Stripe-paid-path branch in handleCreateGroup uses
        // `user.bypass_payment !== true` so undefined here resolves to
        // NOT-bypass → free path (the safe default direction). The old
        // implicit-truthiness form `!user.bypass_payment` flipped the
        // fast-path-before-enrich window's default to the unsafe paid path.
        // Strict === true here + !== true at the use site is the matched
        // pair that keeps the safe default during the absent-field window.
        bypass_payment: prof?.bypass_payment === true,
      }
    } catch (err) {
      log('getCurrentUser error:', err)
      return null
    }
  }
  return read(KEYS.profile)
}

// ── Groups ────────────────────────────────────────────────────────────────────
// groups columns: id, name, contest_type, sport, duration,
//                 entry_fee, created_by, invite_code, created_at, budget, prize_split

export async function createGroup(data, userId, agreementAccepted) {
  const dates = calculateGroupDates(data.duration_type, data.sport, data.start_date)
  // Explicit start_date / end_date override: the duration-based math in
  // calculateGroupDates assumes a generic daily/weekly/season/playoffs
  // window — fine for matchup pools, but wrong for Golf pick_em pools
  // whose lifecycle is the tournament's actual run (Thu-Sun ± weather).
  // handleCreateGroup computes tournament-accurate dates for Golf via
  // src/lib/golfPoolDates.js and threads them through here. Every other
  // contest type falls through to the calculateGroupDates output.
  const startISO = data.start_date ?? dates.start.toISOString().slice(0, 10)
  const endISO   = data.end_date   ?? dates.end.toISOString().slice(0, 10)

  if (isSupabaseEnabled) {
    // Atomic via RPC (see migrations/017_agreed_at_and_rpc_extensions.sql,
    // which supersedes migration 007). RPC wraps groups + group_members +
    // entry_fee transaction inserts in a single Postgres transaction — any
    // failure rolls back all writes. Balance pre-check + auth identity
    // (auth.uid()) live server-side. p_agreement_accepted is REQUIRED —
    // the RPC raises 'Pool agreement required' if not true.
    //
    // Invite-code collisions: groups.invite_code is UNIQUE. The RPC catches
    // unique_violation on the groups insert and re-raises with the sentinel
    // 'INVITE_CODE_COLLISION'; we retry with a fresh makeCode() up to 3
    // times before giving up. Collision odds at ~1B combinations make 3
    // retries effectively infinitesimal.
    for (let attempt = 0; attempt < 3; attempt++) {
      const { data: g, error } = await supabase.rpc('create_group_atomic', {
        p_name:               data.name,
        p_contest_type:       data.contest_type,
        p_sport:              data.sport,
        p_duration:           data.duration,
        p_entry_fee:          data.entry_fee,
        p_budget:             data.budget,
        p_prize_split:        data.prize_split,
        p_start_round:        data.start_round || 1,
        p_invite_code:        makeCode(),
        p_start_date:         startISO,
        p_end_date:           endISO,
        p_max_players:        data.max_players ?? null,
        p_image_url:          data.image_url || null,
        p_agreement_accepted: agreementAccepted,
        p_golf_config:        data.golf_config ?? null,
        p_ufc_config:         data.ufc_config ?? null,
        p_lockout_at:         data.lockout_at ?? null,
      })
      if (!error) return g
      if (error.message === 'INVITE_CODE_COLLISION') continue
      throw new Error(error.message)
    }
    throw new Error('Could not generate unique invite code after 3 attempts')
  }
  seedGroups()
  const group = {
    id:           uid(),
    name:         data.name,
    contest_type: data.contest_type,
    sport:        data.sport,
    duration:     data.duration,
    entry_fee:    data.entry_fee,
    budget:       data.budget,
    prize_split:  data.prize_split,
    start_round:  data.start_round || 1,
    created_by:   userId,
    invite_code:  makeCode(),
    status:       'active',
    start_date:   startISO,
    end_date:     endISO,
    created_at:   now(),
  }
  const groups = read(KEYS.groups) || []
  groups.push(group)
  write(KEYS.groups, groups)
  const members = read(KEYS.members) || []
  // Demo-mode mirror: agreed_at written when the UI gate has confirmed
  // the checkbox (universal gate, applies in both demo and prod).
  members.push({ id:uid(), group_id:group.id, user_id:userId, points:0, correct:0, total:0, joined_at:now(), agreed_at:now() })
  write(KEYS.members, members)
  return group
}

export async function joinGroup(code, userId, agreementAccepted) {
  if (isSupabaseEnabled) {
    // Atomic via RPC (see migrations/017_agreed_at_and_rpc_extensions.sql,
    // which supersedes migration 010). RPC uses SELECT ... FOR UPDATE on
    // the target groups row so parallel joins serialize against the
    // max_players cap. Membership + entry-fee transaction land in one
    // transaction; any failure rolls back both. Auth identity comes from
    // auth.uid() server-side. p_agreement_accepted is REQUIRED — the RPC
    // raises 'Pool agreement required' if not true.
    const { data: g, error } = await supabase.rpc('join_group_atomic', {
      p_invite_code:        code.trim().toUpperCase(),
      p_agreement_accepted: agreementAccepted,
    })
    if (error) throw new Error(error.message)
    return g
  }
  seedGroups()
  const groups = read(KEYS.groups) || []
  const group  = groups.find(g => g.invite_code === code.trim().toUpperCase())
  if (!group) throw new Error('Group not found — check the invite code')
  const members = read(KEYS.members) || []
  if (members.find(m => m.group_id === group.id && m.user_id === userId)) throw new Error('You are already in this group')
  // Demo-mode mirror: agreed_at written when the UI gate (universal,
  // applies in both demo and prod) has confirmed the checkbox.
  members.push({ id:uid(), group_id:group.id, user_id:userId, points:0, correct:0, total:0, joined_at:now(), agreed_at:now() })
  write(KEYS.members, members)
  write(KEYS.groups, groups)
  return group
}

// Lightweight pool metadata lookup for the JoinGroupModal preview card.
// Mirrors the contract of migrations/016_get_pool_preview.sql — see that
// file for the return shape spec. Demo-mode synthesizes the same envelope
// from localStorage seed data so the offline flow renders the same UI.
export async function getPoolPreview(code) {
  const trimmed = (code || '').trim().toUpperCase()
  if (!trimmed) return { ok: false, reason: 'empty code' }

  if (isSupabaseEnabled) {
    const { data, error } = await supabase.rpc('get_pool_preview', {
      p_invite_code: trimmed,
    })
    if (error) {
      log('getPoolPreview error:', error)
      return { ok: false, reason: 'rpc error', error_message: error.message }
    }
    return data
  }

  // Demo-mode: synthesize from KEYS.groups + KEYS.members + KEYS.profile.
  seedGroups()
  const groups = read(KEYS.groups) || []
  const group  = groups.find(g => g.invite_code === trimmed)
  if (!group) return { ok: false, reason: 'group missing' }

  const members      = (read(KEYS.members) || []).filter(m => m.group_id === group.id)
  const currentUser  = read(KEYS.profile) || {}
  const alreadyMember = members.some(m => m.user_id === currentUser.id)

  return {
    ok:             true,
    name:           group.name,
    entry_fee:      group.entry_fee,
    prize_split:    group.prize_split,
    status:         group.status || 'active',
    contest_type:   group.contest_type,
    sport:          group.sport,
    member_count:   members.length,
    max_players:    group.max_players ?? null,
    already_member: alreadyMember,
  }
}

// Deletes a group and all its dependent rows. Allowed only when:
//   1. The caller is the group's creator
//   2. No one else has joined yet (member count <= 1)
//   3. The group is still 'active' (not completed/archived)
// On success, refunds the creator's entry fee via a 'refund' transaction.
export async function deleteGroup(groupId, userId) {
  if (!isSupabaseEnabled || !groupId || !userId) return

  const { data: group, error: gErr } = await supabase
    .from('groups')
    .select('created_by, entry_fee, status, name')
    .eq('id', groupId)
    .single()
  if (gErr || !group) throw new Error('Group not found')

  if (group.created_by !== userId) {
    throw new Error('Only the group creator can delete this group')
  }
  if (group.status && group.status !== 'active') {
    throw new Error('This group is no longer active and cannot be deleted')
  }

  // Member-count gate: 1 means just the creator; >1 means someone else joined.
  const { count, error: cErr } = await supabase
    .from('group_members')
    .select('*', { count: 'exact', head: true })
    .eq('group_id', groupId)
  if (cErr) throw new Error(friendly(cErr))
  if ((count || 0) > 1) {
    throw new Error('You cannot delete a group once other players have joined. Contact support to resolve this.')
  }

  // Soft-delete: flip status to 'archived' instead of cascading deletes.
  // Preserves picks, bracket_picks, group_members rows so wager-history
  // entries stay tappable and GroupDetail can render a read-only view.
  // CHECK constraint groups_status_check (migration 009) enforces the
  // accepted value set.
  const { error: delErr } = await supabase
    .from('groups')
    .update({ status: 'archived' })
    .eq('id', groupId)
  if (delErr) throw new Error(friendly(delErr))

  // Refund the creator's entry fee so the wallet reflects the cancellation.
  // group_id is populated now that we're soft-deleting — the groups row
  // still exists, so the FK link holds and Wager History can navigate
  // back to the archived group page.
  if ((group.entry_fee || 0) > 0) {
    try {
      await supabase.from('transactions').insert({
        user_id:     userId,
        amount:      group.entry_fee,
        type:        'refund',
        description: `${group.name || 'Group'} deleted - entry fee refunded`,
        group_id:    groupId,
      })
    } catch (err) {
      log('deleteGroup refund error:', err)
    }
  }
}

export async function getUserGroups(userId, freemiumOn = false) {
  // freemiumOn gates the inclusion of unlocked_at + lockout_at in the
  // embedded groups (...) select. These columns were added by migration
  // 040 (Phase 1.α) and exist only on schemas where freemium has been
  // rolled out. Including them on a pre-040 prod returns 42703/400 →
  // empty group list (the bug shipped 2026-06-14, frontend reverted
  // same night). Default false so any un-updated caller is safe.
  // App.jsx threads `stripeEnabled` (the resolved /api/config flag) as
  // the actual value; before the config resolves stripeEnabled is
  // false, which is correct for prod-today and also for any first-
  // render race window pre-launch. The launch-time false→true flip is
  // handled by an App-level refetch effect keyed on stripeEnabled
  // (App.jsx, Phase 1.ζ-3 unblock).
  const freemiumCols = freemiumOn ? '\n            unlocked_at,\n            lockout_at,' : ''
  if (isSupabaseEnabled) {
    try {
      const { data, error } = await supabase
        .from('group_members')
        .select(`
          points,
          correct_picks,
          total_picks,
          joined_at,
          groups (
            id,
            name,
            contest_type,
            sport,
            duration,
            start_round,
            entry_fee,
            budget,
            prize_split,
            invite_code,
            created_by,
            created_at,
            status,
            start_date,
            end_date,
            image_url,
            max_players,
            golf_config,
            ufc_config,
            bracket_locked_at,${freemiumCols}
            group_members (count)
          )
        `)
        .eq('user_id', userId)
      if (error) {
        log('getUserGroups error:', error)
        return []
      }
      const baseGroups = (data || []).map(row => {
        const g = row.groups
        if (!g) return null
        const memberCount = g.group_members?.[0]?.count ?? 0
        const { group_members: _, ...groupFields } = g
        return {
          ...groupFields,
          member_count:        memberCount,
          user_points:         row.points         ?? 0,
          user_total_picks:    row.total_picks    ?? 0,
          user_correct_picks:  row.correct_picks  ?? 0,
        }
      }).filter(Boolean)

      const groupIds = baseGroups.map(g => g.id)
      if (groupIds.length === 0) return baseGroups

      // One batched fetch covers both the cross-group rank calculation
      // and the avatar-row usernames. Joining profiles(username) here
      // saves a second roundtrip per render.
      const { data: allMembers } = await supabase
        .from('group_members')
        .select('group_id, user_id, points, profiles(username, avatar_url)')
        .in('group_id', groupIds)

      if (!allMembers) return baseGroups

      const byGroup = new Map()
      for (const m of allMembers) {
        if (!byGroup.has(m.group_id)) byGroup.set(m.group_id, [])
        byGroup.get(m.group_id).push({
          user_id:    m.user_id,
          points:     m.points ?? 0,
          username:   m.profiles?.username,
          avatar_url: m.profiles?.avatar_url || null,
        })
      }

      // For bracket groups, count picks the user actually submitted (from
      // bracket_picks.picks). total_picks on group_members tracks graded
      // picks — a different concept. The GroupCard "X Picks Made" badge
      // wants picks-submitted, not picks-graded.
      const bracketGroupIds = baseGroups
        .filter(g => g.contest_type === 'bracket')
        .map(g => g.id)
      const picksSubmittedByGroup = new Map()
      if (bracketGroupIds.length > 0) {
        const { data: bracketRows } = await supabase
          .from('bracket_picks')
          .select('group_id, picks')
          .eq('user_id', userId)
          .in('group_id', bracketGroupIds)
        for (const row of (bracketRows || [])) {
          const p = row.picks
          // v2 shape: { v: 2, picks: [{round, slot, team}, ...] }
          // v1 shape (legacy): { matchupId: team, ..., __lengths?: {} }
          let count = 0
          if (p?.v === 2 && Array.isArray(p.picks)) {
            count = p.picks.length
          } else if (p && typeof p === 'object') {
            // v1: count keys, excluding the __lengths metadata sidecar
            count = Object.keys(p).filter(k => !k.startsWith('__')).length
          }
          picksSubmittedByGroup.set(row.group_id, count)
        }
      }

      return baseGroups.map(g => {
        const members = byGroup.get(g.id) || []
        const sorted  = [...members].sort((a, b) => b.points - a.points)
        const idx     = sorted.findIndex(m => m.user_id === userId)
        return {
          ...g,
          user_rank: idx === -1 ? null : idx + 1,
          user_picks_submitted: picksSubmittedByGroup.get(g.id) ?? null,
          // Lightweight roster for the avatar row (top 5 by points).
          members:   sorted.slice(0, 5).map(m => ({
            user_id:    m.user_id,
            username:   m.username || 'Player',
            avatar_url: m.avatar_url,
          })),
        }
      })
    } catch (err) {
      log('getUserGroups failed:', err)
      return []
    }
  }
  seedGroups()
  const members = read(KEYS.members) || []
  const myIds   = members.filter(m => m.user_id === userId).map(m => m.group_id)
  return (read(KEYS.groups) || []).filter(g => myIds.includes(g.id))
}

// contestType is OPTIONAL. When 'divisions' the sort is divisions_score ASC
// (low strokes wins; NULL scores sort last so pre-grade rows park at the
// bottom in a stable order, tie-broken by joined_at ASC). For every other
// contest type (and for an undefined contestType) the historical points
// DESC sort is preserved byte-identical — pick'em / bracket / golf matchup
// callers see no change. The SELECT adds divisions_score unconditionally
// (additive: consumers that don't read it are unaffected; the divisions
// standings UI needs it on every load to render the score column).
export async function getGroupMembers(groupId, contestType) {
  const isDivisions = contestType === 'divisions'
  if (isSupabaseEnabled) {
    try {
      let q = supabase
        .from('group_members')
        .select('id, group_id, user_id, points, divisions_score, joined_at, correct_picks, total_picks, final_rank, profiles(username, avatar_url)')
        .eq('group_id', groupId)
      q = isDivisions
        ? q.order('divisions_score', { ascending: true, nullsFirst: false })
           .order('joined_at',       { ascending: true })
        : q.order('points', { ascending: false })
      const { data, error } = await q
      if (error) { log('getGroupMembers error:', error); return [] }
      return (data || []).map(m => ({
        id:              m.id,
        group_id:        m.group_id,
        user_id:         m.user_id,
        points:          m.points,
        divisions_score: m.divisions_score,
        joined_at:       m.joined_at,
        correct_picks:   m.correct_picks,
        total_picks:     m.total_picks,
        final_rank:      m.final_rank ?? null,
        username:        m.profiles?.username     || m.user_id,
        avatar_url:      m.profiles?.avatar_url   || null,
        correct:         m.correct_picks ?? 0,   // UI compatibility
        total:           m.total_picks   ?? 0,   // UI compatibility
      }))
    } catch (err) {
      log('getGroupMembers error:', err)
      return []
    }
  }
  // localStorage fallback — almost never hit for divisions (Supabase is
  // required for the divisions data path), but branched for consistency
  // so a future local-dev environment with divisions data doesn't sort
  // by points and silently rank everyone backwards.
  const members  = read(KEYS.members) || []
  const filtered = members.filter(m => m.group_id === groupId)
  return isDivisions
    ? filtered.sort((a, b) => {
        const aScore = a.divisions_score ?? Number.POSITIVE_INFINITY
        const bScore = b.divisions_score ?? Number.POSITIVE_INFINITY
        if (aScore !== bScore) return aScore - bScore
        return (a.joined_at || '').localeCompare(b.joined_at || '')
      })
    : filtered.sort((a, b) => b.points - a.points)
}

// Returns the display name of whoever paid the winning unlock for `groupId`,
// or null if no winning row exists (pool not yet unlocked, grandfathered
// pool, or attribution unreadable). Mirrors the §10.4 "Unlocked by Alice"
// attribution UI; the consumer renders nothing when this returns null —
// never a "Unlocked by Someone" placeholder.
//
// Two-query split (was a single embedded join): pool_unlocks.unlocked_by_
// user_id references auth.users(id), NOT public.profiles(id). PostgREST
// can't resolve the embed `profiles(username)` because there's no FK from
// pool_unlocks to profiles — only the shared-PK Supabase convention,
// which isn't a declared constraint. The single-query form 400s. The
// two-query form below is one extra HTTP roundtrip (fires once per pool
// open) but reliable and schema-change-free.
//
// Columns confirmed against migrations/041_pool_unlocks.sql:
//   • pool_unlocks.group_id           — FK to groups(id)
//   • pool_unlocks.unlocked_by_user_id — FK to auth.users(id) (NULLable on
//                                        ON DELETE SET NULL)
//   • pool_unlocks.was_winning_payment — boolean; exactly one row per group
//                                        gets true via the RPC's race-safe
//                                        UPDATE (§10.5 audit insert).
//
// .maybeSingle() is intentional in both queries — "no winning row" is the
// COMMON case (every grandfathered pool, every pre-unlock pool). .single()
// would throw PGRST116 for those and pollute the console with errors on a
// totally expected state.
export async function getPoolUnlockAttribution(groupId) {
  if (!isSupabaseEnabled) return null
  try {
    // Step 1: winning row's user id.
    const { data: unlockRow, error: unlockErr } = await supabase
      .from('pool_unlocks')
      .select('unlocked_by_user_id')
      .eq('group_id', groupId)
      .eq('was_winning_payment', true)
      .maybeSingle()
    if (unlockErr) { log('getPoolUnlockAttribution unlock-row error:', unlockErr); return null }
    const userId = unlockRow?.unlocked_by_user_id
    if (!userId) return null
    // Step 2: resolve the username.
    const { data: profileRow, error: profileErr } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', userId)
      .maybeSingle()
    if (profileErr) { log('getPoolUnlockAttribution profile error:', profileErr); return null }
    return profileRow?.username || null
  } catch (err) {
    log('getPoolUnlockAttribution error:', err)
    return null
  }
}

// group_members columns: id, group_id, user_id, points, joined_at, correct_picks, total_picks
export async function updateMemberPoints(groupId, userId, delta, correctDelta = 0, totalDelta = 0) {
  if (isSupabaseEnabled) {
    try {
      const { data: m, error } = await supabase
        .from('group_members')
        .select('points, correct_picks, total_picks')
        .eq('group_id', groupId)
        .eq('user_id', userId)
        .single()
      if (error || !m) { log('updateMemberPoints: member not found', groupId, userId); return }
      const { error: upErr } = await supabase
        .from('group_members')
        .update({
          points:        (m.points        || 0) + delta,
          correct_picks: (m.correct_picks || 0) + correctDelta,
          total_picks:   (m.total_picks   || 0) + totalDelta,
        })
        .eq('group_id', groupId)
        .eq('user_id', userId)
      if (upErr) log('updateMemberPoints error:', upErr)
    } catch (err) {
      log('updateMemberPoints error:', err)
    }
    return
  }
  const members = read(KEYS.members) || []
  const idx = members.findIndex(m => m.group_id === groupId && m.user_id === userId)
  if (idx >= 0) {
    members[idx].points  = (members[idx].points  || 0) + delta
    members[idx].correct = (members[idx].correct || 0) + correctDelta
    members[idx].total   = (members[idx].total   || 0) + totalDelta
    write(KEYS.members, members)
  }
}

// ── Picks ─────────────────────────────────────────────────────────────────────
// picks columns: id, user_id, group_id, game_id, picked_team, result, points_earned, created_at
// Unique constraint required: (user_id, group_id, game_id)
// Run in Supabase SQL editor:
//   alter table picks add constraint picks_unique unique (user_id, group_id, game_id);

export async function savePicks(groupId, userId, data, roundKey = 'daily') {
  localStorage.setItem(`wb_picks_${groupId}_${userId}_${roundKey}`, JSON.stringify(data))

  if (!isSupabaseEnabled || !userId || !groupId) return

  const picksObj = data.picks ?? data
  const entries  = Object.entries(picksObj).filter(([gameId, pick]) => pick && gameId)
  if (!entries.length) return

  try {
    const rows = entries.map(([gameId, pick]) => ({
      user_id:          userId,
      group_id:         groupId,
      game_id:          gameId,
      picked_team:      typeof pick === 'string' ? pick : (pick.side || ''),
      picked_team_name: typeof pick === 'string' ? null : (pick.team || null),
      result:           typeof pick === 'string' ? 'pending' : (pick.status || 'pending'),
      points_earned:    typeof pick === 'string' ? 0 : (pick.pointsAwarded ?? 0),
      round_points:     typeof pick === 'string' ? 1 : (pick.roundPoints ?? 1),
      // ?? '' (not || '') so we keep legitimately set empty values rather
      // than collapsing to null. The grader compares strict equality on
      // these fields; null on either side silently kills the match.
      home_team:        typeof pick === 'string' ? '' : (pick.homeTeam ?? ''),
      away_team:        typeof pick === 'string' ? '' : (pick.awayTeam ?? ''),
      game_date:        typeof pick === 'string' ? null : (pick.gameDate || null),
    }))
    const { error } = await supabase
      .from('picks')
      .upsert(rows, { onConflict: 'user_id,group_id,game_id' })
    if (error) log('savePicks error:', error)
  } catch (err) {
    log('savePicks error:', err)
  }
}

// ── Golf picks ────────────────────────────────────────────────────────────────
// Stage 5 Step 3 — mirrors savePicks's discipline (localStorage mirror first,
// supabase-disabled fast path, no-op-empty fast path, bulk upsert under user
// RLS, error swallowing).
//
// Differences from savePicks:
//   - picks is an ARRAY of grouping picks (one per pairing the user picked in
//     this round), not a keyed object — golf's natural key is the composite
//     (round, group_id_golf) so an array reads more naturally than an object.
//   - localStorage key includes round (one entry per round) so a partial
//     save in R1 doesn't clobber R2's draft.
//   - submitted is a localStorage-only concept — golf_picks has no submitted
//     column. Step 4's UI tracks soft per-round lock state client-side; the
//     DB write is independent of the flag.
//   - grouping_members passes as a raw array (not JSON.stringify'd) —
//     supabase-js serializes to jsonb; double-encoding would store a string
//     the Stage 6 grader can't read (same lesson as Stage 3's golf_config).
//   - Malformed rows (missing group_id_golf or selected_dg_id, the grader's
//     lookup keys) are filtered + logged loudly, not silently dropped.
export async function saveGolfPicks(groupId, userId, eventId, round, picks, submitted = false) {
  localStorage.setItem(
    `wb_golfpicks_${groupId}_${userId}_${round}`,
    JSON.stringify({ picks, submitted })
  )

  if (!isSupabaseEnabled || !userId || !groupId) return
  if (!Array.isArray(picks) || !picks.length) return

  try {
    const rawRows = picks.map(p => ({
      user_id:          userId,
      group_id:         groupId,
      event_id:         eventId,
      round:            round,
      group_id_golf:    p.groupIdGolf,
      selected_dg_id:   p.selectedDgId,
      selected_name:    p.selectedName ?? '',
      grouping_members: Array.isArray(p.groupingMembers) ? p.groupingMembers : [],
      // result / points_earned / round_points OMITTED — DB defaults apply.
      // The Stage 6 grader writes result + points_earned via the service role.
    }))

    // Filter malformed rows. group_id_golf is the per-round pairing identity
    // the grader keys on; selected_dg_id is the picked-golfer identity it
    // compares against. A row missing either is unparseable downstream. Log
    // loudly (not silent) so the calling code's bug is visible.
    const rows = rawRows.filter(r => r.group_id_golf && r.selected_dg_id)
    if (rows.length < rawRows.length) {
      log('saveGolfPicks: skipped malformed pick(s) —',
          rawRows.length - rows.length, 'of', rawRows.length, 'missing group_id_golf or selected_dg_id')
    }
    if (!rows.length) return

    const { error } = await supabase
      .from('golf_picks')
      .upsert(rows, { onConflict: 'user_id,group_id,round,group_id_golf' })
    if (error) log('saveGolfPicks error:', error)
  } catch (err) {
    log('saveGolfPicks error:', err)
  }
}

// Stage 5 — read counterpart to saveGolfPicks. Mirrors loadPicks's
// discipline: localStorage read first (returned as fallback if Supabase
// disabled / errors), explicit snake→camel mapping, `?? ''` / `?? null`
// guards on every nullable field, Array.isArray guard on grouping_members
// against null/legacy rows. Returns null if no localStorage AND no DB rows.
//
// Return shape: { picks: { groupIdGolf: pickObj, ... }, submitted }
//   - picks is keyed by groupIdGolf for O(1) "did I pick this pairing?"
//     lookup in the UI (mirrors loadPicks's game_id-keyed map).
//   - submitted comes from localStorage (golf_picks has no submitted
//     column — same as picks/bracket_picks; the soft per-round lock
//     lives client-side only).
//   - result/pointsEarned/roundPoints are included in the pickObj for
//     Stage 6's eventual scoring display; Step 4's UI doesn't render
//     them yet but the load surface is forward-compatible.
export async function loadGolfPicks(groupId, userId, round) {
  let meta = null
  try {
    meta = JSON.parse(localStorage.getItem(`wb_golfpicks_${groupId}_${userId}_${round}`) || 'null')
  } catch {}

  if (!isSupabaseEnabled || !userId || !groupId) return meta

  try {
    const { data, error } = await supabase
      .from('golf_picks')
      .select('group_id_golf, selected_dg_id, selected_name, grouping_members, result, points_earned, round_points, round_scores')
      .eq('user_id',  userId)
      .eq('group_id', groupId)
      .eq('round',    round)
    if (error || !data?.length) return meta

    const picks = {}
    for (const row of data) {
      picks[row.group_id_golf] = {
        groupIdGolf:     row.group_id_golf,
        selectedDgId:    row.selected_dg_id,
        selectedName:    row.selected_name ?? '',
        groupingMembers: Array.isArray(row.grouping_members) ? row.grouping_members : [],
        result:          row.result        ?? 'pending',
        pointsEarned:    row.points_earned ?? 0,
        roundPoints:     row.round_points  ?? 1,
        // Migration 033 — { dgId: to-par int | null } for the pairing
        // members. NULL on pre-feature graded picks → display renders "—".
        roundScores:     row.round_scores  ?? null,
      }
    }
    return { picks, submitted: meta?.submitted ?? false }
  } catch (err) {
    log('loadGolfPicks error:', err)
    return meta
  }
}

// ── Golf divisions picks (Phase 3, migration 032) ────────────────────────
// Save/load surface for the divisions roster-mode entry. Distinct from
// saveGolfPicks/loadGolfPicks (matchup mode) — different table
// (golf_division_picks vs golf_picks), different key shape
// ((group, user, event, tier) vs (group, user, round, group_id_golf)),
// different UI semantics (one-pick-per-tier vs one-pick-per-pairing).
//
// SAVE-TIME VALIDATION CONTRACT
//
// saveDivisionPicks validates the entry CLIENT-SIDE against the frozen
// groups.golf_config.tierSnapshot before writing. Three invariants:
//   1. Exactly 6 picks, one per tier (tiers 1-6 present, no duplicates).
//   2. Each pick.selected_dg_id appears in tierSnapshot[String(tier)].
//   3. score_to_par_guess (optional) is a finite integer if provided —
//      collected/stored ONLY (Phase 3); NOT wired into any tiebreak or
//      grader computation (Phase 4 scope).
//
// **PHASE 4 / HARDENING GAP** (documented in HANDOFF design-question
// section). Snapshot-membership validation is client-side ONLY in
// Phase 3. A malicious client can write a row with selected_dg_id
// NOT in tierSnapshot[tier]; the DB doesn't enforce this (jsonb has
// no foreign-key relationship to it by construction). For Phase 4
// or a hardening pass: move the validation into a SECURITY DEFINER
// RPC (save_division_picks_atomic) that reads golf_config.tierSnapshot
// server-side and rejects malformed inserts with a clear sentinel,
// mirroring the create_group_atomic discipline.
//
// PARAMS
//   groupId, userId, eventId: same shape as saveGolfPicks
//   picks: Array<{ tier: 1-6, selectedDgId: string, selectedName: string }>
//          — exactly 6 entries, one per tier
//   tieGuess: integer | null — score-to-par tiebreaker (stored to
//             group_members.score_to_par_guess; Phase 3 collect-only)
//   tierSnapshot: { "1": [dgIds...], ..., "6": [dgIds...] } — the
//                 frozen-at-create snapshot from groups.golf_config.
//                 REQUIRED for client-side membership validation.
//
// RETURN: { ok: true } on success | { ok: false, error: { code, detail } }
//   Error codes:
//     INVALID_PICK_COUNT  — picks.length !== 6
//     INVALID_TIER_SET    — tiers 1-6 not all present, or duplicates
//     INVALID_DG_ID       — selected_dg_id not in tierSnapshot[tier]
//     INVALID_TIE_GUESS   — tieGuess is provided but not a finite int
//     INVALID_SNAPSHOT    — tierSnapshot is missing/malformed
//     DB_ERROR            — supabase upsert/update failed
export async function saveDivisionPicks(groupId, userId, eventId, picks, tieGuess, tierSnapshot) {
  // ── 1. Shape validation (client-side gate; DB has no equivalent) ──
  if (!Array.isArray(picks) || picks.length !== 6) {
    return { ok: false, error: { code: 'INVALID_PICK_COUNT', detail: `expected exactly 6 picks, got ${picks?.length ?? 'n/a'}` } }
  }
  const seenTiers = new Set()
  for (const p of picks) {
    if (!p || typeof p !== 'object') {
      return { ok: false, error: { code: 'INVALID_TIER_SET', detail: 'pick row is not an object' } }
    }
    if (!Number.isInteger(p.tier) || p.tier < 1 || p.tier > 6) {
      return { ok: false, error: { code: 'INVALID_TIER_SET', detail: `tier must be integer 1-6, got ${p.tier}` } }
    }
    if (seenTiers.has(p.tier)) {
      return { ok: false, error: { code: 'INVALID_TIER_SET', detail: `tier ${p.tier} appears more than once` } }
    }
    seenTiers.add(p.tier)
    if (!p.selectedDgId || typeof p.selectedDgId !== 'string') {
      return { ok: false, error: { code: 'INVALID_TIER_SET', detail: `tier ${p.tier} missing selectedDgId` } }
    }
  }
  if (seenTiers.size !== 6) {
    return { ok: false, error: { code: 'INVALID_TIER_SET', detail: `tiers covered: ${[...seenTiers].sort().join(',')}; require 1,2,3,4,5,6` } }
  }

  // ── 2. Snapshot validation (client-side; Phase 4/hardening gap: move server-side) ──
  if (!tierSnapshot || typeof tierSnapshot !== 'object') {
    return { ok: false, error: { code: 'INVALID_SNAPSHOT', detail: 'tierSnapshot is missing or malformed' } }
  }
  for (const p of picks) {
    const tierKey = String(p.tier)
    const slot    = tierSnapshot[tierKey]
    if (!Array.isArray(slot)) {
      return { ok: false, error: { code: 'INVALID_SNAPSHOT', detail: `tierSnapshot["${tierKey}"] is not an array` } }
    }
    if (!slot.includes(p.selectedDgId)) {
      return { ok: false, error: { code: 'INVALID_DG_ID', detail: `selected_dg_id ${p.selectedDgId} is not in tier ${p.tier} snapshot (slot has ${slot.length} entries)` } }
    }
  }

  // ── 3. Tie-guess validation (optional; Phase 3 collect-only) ──
  if (tieGuess != null) {
    if (!Number.isFinite(tieGuess) || !Number.isInteger(tieGuess)) {
      return { ok: false, error: { code: 'INVALID_TIE_GUESS', detail: `tieGuess must be a finite integer, got ${tieGuess}` } }
    }
  }

  // ── 4. LocalStorage mirror (offline read parity with saveGolfPicks) ──
  try {
    localStorage.setItem(
      `wb_divisionpicks_${groupId}_${userId}_${eventId}`,
      JSON.stringify({ picks, tieGuess })
    )
  } catch {}

  if (!isSupabaseEnabled || !userId || !groupId || !eventId) return { ok: true }

  // ── 5. Route the write through the server endpoint (Slice B). ──
  // The client-side shape + snapshot + tie-guess validation above is a
  // UX fast-fail courtesy ONLY — explicitly NOT a security gate. The
  // server endpoint re-validates everything and applies the tee-time
  // gate that a client-side check could not safely enforce (same lesson
  // as the Slice 1 reveal endpoint: client gates are bypassable, only
  // server-enforced gates count).
  //
  // The tierSnapshot argument to this function is no longer sent to the
  // server (it would have been ignored anyway — the endpoint resolves
  // from golf_config server-side to close the Phase 4 hardening gap).
  // It's still used for the client-side fast-fail snapshot check above
  // so a malformed entry is caught before the network round-trip.
  //
  // Post-Slice-B-Step-2 (migration 048's REVOKE INSERT, UPDATE FROM
  // authenticated): this is the only client path that can succeed —
  // direct supabase.from('golf_division_picks').upsert(...) returns
  // 'permission denied for table golf_division_picks'.
  return submitMemberDivisionPicks(groupId, picks, tieGuess)
}

// Load counterpart to saveDivisionPicks. Read from golf_division_picks +
// group_members.score_to_par_guess. Localstorage fallback if supabase is
// disabled or errors.
//
// Return shape: { picks: { "<tier>": { tier, selectedDgId, selectedName,
//                                       result, totalStrokes } },
//                 tieGuess: integer|null }
//   - picks is keyed by tier string for O(1) "did I pick T3?" lookup
//     in the UI (mirrors loadGolfPicks's groupIdGolf-keyed map).
//   - result/totalStrokes are forward-compat: Phase 4 grader writes
//     them; Phase 3 picker doesn't render them yet, but the load
//     surface exposes the fields.
//   - tieGuess comes from group_members.score_to_par_guess; null if
//     no row or not yet set.
export async function loadDivisionPicks(groupId, userId, eventId) {
  // localStorage mirror
  let mirror = null
  try {
    mirror = JSON.parse(localStorage.getItem(`wb_divisionpicks_${groupId}_${userId}_${eventId}`) || 'null')
  } catch {}
  const mirrorPicks = (() => {
    if (!mirror?.picks || !Array.isArray(mirror.picks)) return {}
    const m = {}
    for (const p of mirror.picks) m[String(p.tier)] = p
    return m
  })()
  const mirrorBundle = {
    picks:    mirrorPicks,
    tieGuess: mirror?.tieGuess ?? null,
  }

  if (!isSupabaseEnabled || !userId || !groupId || !eventId) return mirrorBundle

  try {
    const { data: pickRows, error: pickErr } = await supabase
      .from('golf_division_picks')
      .select('tier, selected_dg_id, selected_name, result, total_strokes')
      .eq('user_id',  userId)
      .eq('group_id', groupId)
      .eq('event_id', String(eventId))
    if (pickErr) {
      log('loadDivisionPicks pick error:', pickErr)
      return mirrorBundle
    }

    const picks = {}
    for (const row of (pickRows || [])) {
      picks[String(row.tier)] = {
        tier:         row.tier,
        selectedDgId: row.selected_dg_id,
        selectedName: row.selected_name ?? '',
        result:       row.result        ?? 'pending',
        totalStrokes: row.total_strokes ?? null,
      }
    }

    let tieGuess = null
    try {
      const { data: memberRow, error: memberErr } = await supabase
        .from('group_members')
        .select('score_to_par_guess')
        .eq('group_id', groupId)
        .eq('user_id',  userId)
        .maybeSingle()
      if (memberErr) {
        log('loadDivisionPicks tie-guess read error:', memberErr)
      } else {
        tieGuess = memberRow?.score_to_par_guess ?? null
      }
    } catch (err) {
      log('loadDivisionPicks tie-guess read error:', err)
    }

    return { picks, tieGuess }
  } catch (err) {
    log('loadDivisionPicks error:', err)
    return mirrorBundle
  }
}

// Load a member's divisions entry as a flat list of up to 6 rows ordered by
// tier ASC. Surface for the DivisionsMemberResultsSheet drill-down (sibling
// to loadGradedPicks's MemberResultsSheet surface — divisions has no
// game_results enrichment, since the per-pick total IS the outcome and
// kept/dropped is derived client-side by sorting on total_strokes).
//
// Pre-grade rows have total_strokes=null + result='pending'; post-grade
// they're integer + 'graded'. Partial state should not occur (the grader
// writes all 6 in one iteration after the completeness gate clears) but
// the caller is defensive — kept/dropped only renders when ALL 6 have
// integer total_strokes.
//
// Empty array on error or missing input — same fail-open posture as
// loadGradedPicks.
export async function loadDivisionGradedPicks(groupId, userId, eventId) {
  if (!isSupabaseEnabled || !userId || !groupId || !eventId) return []
  try {
    const { data, error } = await supabase
      .from('golf_division_picks')
      .select('id, tier, selected_name, selected_dg_id, total_strokes, result')
      .eq('group_id', groupId)
      .eq('user_id',  userId)
      .eq('event_id', String(eventId))
      .order('tier', { ascending: true })
    if (error) { log('loadDivisionGradedPicks error:', error); return [] }
    return data || []
  } catch (err) {
    log('loadDivisionGradedPicks error:', err)
    return []
  }
}

// ── Group chat (A1) ──────────────────────────────────────────────────────
// Backed by group_messages (migration 023). RLS gates everything — members
// can SELECT/INSERT; users can soft-delete their own messages via
// UPDATE deleted_at; group creator can soft-delete any.
//
// Realtime subscription: postgres-changes channel filtered by group_id.
// Requires the post-migration operator step at the top of migration 023
// (ALTER PUBLICATION supabase_realtime ADD TABLE public.group_messages).

// Load the most-recent N messages for a group, oldest-first so the array
// renders naturally with newest-at-bottom. No pagination in A1.
export async function loadGroupMessages(groupId, limit = 50) {
  if (!isSupabaseEnabled || !groupId) return []
  try {
    const { data, error } = await supabase
      .from('group_messages')
      .select('id, group_id, user_id, body, created_at, deleted_at')
      .eq('group_id', groupId)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) { log('loadGroupMessages error:', error); return [] }
    // Reverse so consumers iterate oldest → newest.
    return (data || []).slice().reverse()
  } catch (err) {
    log('loadGroupMessages error:', err)
    return []
  }
}

// ── Moderation (migration 053) ─────────────────────────────────────────
// content_reports + user_blocks — RLS-guarded direct writes, no server
// endpoint. Report inserts capture a content_snapshot so the operator can
// see flagged text even after the reported message is soft-deleted.

export async function fileContentReport({
  reporterUserId, reportedUserId, groupId, messageId, contentSnapshot, reason,
}) {
  if (!isSupabaseEnabled) return { error: new Error('Supabase not configured') }
  if (!reporterUserId) return { error: new Error('reporterUserId required') }
  const { error } = await supabase.from('content_reports').insert({
    reporter_user_id: reporterUserId,
    reported_user_id: reportedUserId || null,
    group_id:         groupId || null,
    message_id:       messageId || null,
    content_snapshot: contentSnapshot || null,
    reason:           reason || 'other',
  })
  if (error) log('fileContentReport error:', error)
  return { error }
}

export async function blockUser(blockerUserId, blockedUserId) {
  if (!isSupabaseEnabled) return { error: new Error('Supabase not configured') }
  if (!blockerUserId || !blockedUserId) return { error: new Error('both userIds required') }
  if (blockerUserId === blockedUserId) return { error: new Error('cannot block yourself') }
  const { error } = await supabase.from('user_blocks').insert({
    blocker_user_id: blockerUserId,
    blocked_user_id: blockedUserId,
  })
  // 23505 = unique_violation → already blocked. Not a real error for the UI.
  if (error && error.code === '23505') return { error: null, alreadyBlocked: true }
  if (error) log('blockUser error:', error)
  return { error }
}

export async function unblockUser(blockerUserId, blockedUserId) {
  if (!isSupabaseEnabled) return { error: new Error('Supabase not configured') }
  if (!blockerUserId || !blockedUserId) return { error: new Error('both userIds required') }
  const { error } = await supabase.from('user_blocks').delete()
    .eq('blocker_user_id', blockerUserId)
    .eq('blocked_user_id', blockedUserId)
  if (error) log('unblockUser error:', error)
  return { error }
}

// Returns [{ blocked_user_id, blocked_username, blocked_avatar_url, created_at }, ...]
// Two-query split (blocks + profiles) — RLS on user_blocks limits the read
// to the caller's own rows automatically.
export async function getBlockedUsers(userId) {
  if (!isSupabaseEnabled) return { data: [], error: new Error('Supabase not configured') }
  if (!userId) return { data: [], error: null }
  const { data: blocks, error } = await supabase.from('user_blocks')
    .select('blocked_user_id, created_at')
    .eq('blocker_user_id', userId)
  if (error) { log('getBlockedUsers error:', error); return { data: [], error } }
  if (!blocks || blocks.length === 0) return { data: [], error: null }
  const ids = blocks.map(b => b.blocked_user_id)
  const { data: profs, error: pErr } = await supabase.from('profiles')
    .select('id, username, avatar_url')
    .in('id', ids)
  if (pErr) { log('getBlockedUsers profiles error:', pErr); return { data: [], error: pErr } }
  const profById = new Map((profs || []).map(p => [p.id, p]))
  return {
    data: blocks.map(b => ({
      blocked_user_id:    b.blocked_user_id,
      blocked_username:   profById.get(b.blocked_user_id)?.username || null,
      blocked_avatar_url: profById.get(b.blocked_user_id)?.avatar_url || null,
      created_at:         b.created_at,
    })),
    error: null,
  }
}

// Lightweight version — just returns the Set of blocked user ids for
// filtering chat message renders. Called on app-load + after block/unblock.
export async function getBlockedUserIds(userId) {
  if (!isSupabaseEnabled || !userId) return new Set()
  const { data, error } = await supabase.from('user_blocks')
    .select('blocked_user_id')
    .eq('blocker_user_id', userId)
  if (error) { log('getBlockedUserIds error:', error); return new Set() }
  return new Set((data || []).map(r => r.blocked_user_id))
}


// Insert a new message. Client-side validation mirrors the schema CHECK
// (trim + non-empty + ≤2000); server enforces it again via the constraint.
// RLS rejects non-members and archived groups — the error propagates back
// in the {error} field for the UI to surface.
export async function sendGroupMessage(groupId, userId, body) {
  if (!isSupabaseEnabled) return { data: null, error: new Error('Supabase not configured') }
  if (!groupId || !userId) return { data: null, error: new Error('groupId and userId required') }
  const trimmed = (body || '').trim()
  if (!trimmed)             return { data: null, error: new Error('Message cannot be empty') }
  if (trimmed.length > 2000) return { data: null, error: new Error('Message exceeds 2000 characters') }
  try {
    const { data, error } = await supabase
      .from('group_messages')
      .insert({ group_id: groupId, user_id: userId, body: trimmed })
      .select('id, group_id, user_id, body, created_at, deleted_at')
      .single()
    if (error) log('sendGroupMessage error:', error)
    return { data, error }
  } catch (err) {
    log('sendGroupMessage error:', err)
    return { data: null, error: err }
  }
}

// Soft-delete: sets deleted_at to now(). RLS chooses authorization —
// own-message policy OR creator-of-group policy. Body stays intact;
// the UI hides body when deleted_at is non-null.
export async function softDeleteMessage(messageId) {
  if (!isSupabaseEnabled) return { error: new Error('Supabase not configured') }
  if (!messageId)         return { error: new Error('messageId required') }
  try {
    const { error } = await supabase
      .from('group_messages')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', messageId)
    if (error) log('softDeleteMessage error:', error)
    return { error }
  } catch (err) {
    log('softDeleteMessage error:', err)
    return { error: err }
  }
}

// Realtime channel for INSERT + UPDATE on this group's messages. Caller
// supplies onMessage(kind, msg) where kind is 'insert' | 'update' and msg
// is payload.new (the row's post-event shape). Returns the channel object;
// caller is responsible for cleanup via supabase.removeChannel(channel).
//
// The filter runs server-side at the publication; combined with the SELECT
// RLS policy, only members receive events for groups they belong to. A
// non-member with the channel name would simply never get events.
export function subscribeToGroupMessages(groupId, onMessage) {
  if (!isSupabaseEnabled || !groupId) return null
  const channel = supabase
    .channel(`group-messages-${groupId}`)
    .on('postgres_changes', {
      event:  'INSERT',
      schema: 'public',
      table:  'group_messages',
      filter: `group_id=eq.${groupId}`,
    }, payload => onMessage('insert', payload.new))
    .on('postgres_changes', {
      event:  'UPDATE',
      schema: 'public',
      table:  'group_messages',
      filter: `group_id=eq.${groupId}`,
    }, payload => onMessage('update', payload.new))
    .subscribe()
  return channel
}

// ── Group chat reactions (A2) ────────────────────────────────────────────
// Backed by message_reactions (migration 024). RLS gates everything —
// members SELECT reactions in their groups; users INSERT/DELETE own.
// No UPDATE policy: reactions are immutable; toggle is delete+insert.
// group_id on the row is denormalized via BEFORE INSERT trigger; clients
// MUST NOT pass group_id (and the column-restricted INSERT grant prevents
// it anyway). See migration 024 header for the full architecture rationale.

// Combined load — fetch the recent messages window AND all reactions on
// those messages in one round trip (two queries, single function). Used
// to replace loadGroupMessages on initial chat mount so the first paint
// has reactions ready instead of flickering them in later.
export async function loadGroupMessagesWithReactions(groupId, limit = 50) {
  if (!isSupabaseEnabled || !groupId) return { messages: [], reactions: [] }
  try {
    const [msgsRes, reactionsRes] = await Promise.all([
      supabase
        .from('group_messages')
        .select('id, group_id, user_id, body, created_at, deleted_at')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false })
        .limit(limit),
      supabase
        .from('message_reactions')
        .select('id, message_id, user_id, emoji, created_at')
        .eq('group_id', groupId)
        .order('created_at', { ascending: true }),
    ])
    if (msgsRes.error) {
      log('loadGroupMessagesWithReactions messages error:', msgsRes.error)
      return { messages: [], reactions: [] }
    }
    if (reactionsRes.error) {
      log('loadGroupMessagesWithReactions reactions error:', reactionsRes.error)
      // Non-fatal — return messages, empty reactions. UI degrades gracefully.
      return { messages: (msgsRes.data || []).slice().reverse(), reactions: [] }
    }
    // Reverse messages so consumers iterate oldest → newest (matches A1).
    // Reactions stay in created_at-asc order so chip-grouping is stable.
    return {
      messages:  (msgsRes.data || []).slice().reverse(),
      reactions: reactionsRes.data || [],
    }
  } catch (err) {
    log('loadGroupMessagesWithReactions error:', err)
    return { messages: [], reactions: [] }
  }
}

// Add a reaction. Caller passes only the three columns the INSERT grant
// allows — group_id is filled by the BEFORE INSERT trigger.
// UNIQUE (message_id, user_id, emoji) catches double-react attempts (e.g.
// stale local state); the error surfaces in the {error} field for the UI
// to roll back its optimistic chip.
export async function addReaction(messageId, userId, emoji) {
  if (!isSupabaseEnabled)            return { data: null, error: new Error('Supabase not configured') }
  if (!messageId || !userId || !emoji) return { data: null, error: new Error('messageId, userId, emoji required') }
  try {
    const { data, error } = await supabase
      .from('message_reactions')
      .insert({ message_id: messageId, user_id: userId, emoji })
      .select('id, message_id, user_id, emoji, created_at, group_id')
      .single()
    if (error) log('addReaction error:', error)
    return { data, error }
  } catch (err) {
    log('addReaction error:', err)
    return { data: null, error: err }
  }
}

// Remove a reaction (toggle-off). Silent no-op if the row's already gone —
// also expected stale-state behavior, no error UI needed.
export async function removeReaction(messageId, userId, emoji) {
  if (!isSupabaseEnabled)            return { error: new Error('Supabase not configured') }
  if (!messageId || !userId || !emoji) return { error: new Error('messageId, userId, emoji required') }
  try {
    const { error } = await supabase
      .from('message_reactions')
      .delete()
      .eq('message_id', messageId)
      .eq('user_id',    userId)
      .eq('emoji',      emoji)
    if (error) log('removeReaction error:', error)
    return { error }
  } catch (err) {
    log('removeReaction error:', err)
    return { error: err }
  }
}

// Realtime channel for INSERT + DELETE on this group's reactions. Caller
// supplies onReaction(kind, row) where kind is 'insert' | 'delete' and row
// is payload.new (INSERT) or payload.old (DELETE — Postgres only ships
// the OLD row's PK columns by default; full row needs REPLICA IDENTITY
// FULL on the table. Caller dedups by id either way).
//
// Filter uses the denormalized group_id (trigger-populated). Combined
// with the SELECT RLS policy, only members get events for their groups.
export function subscribeToGroupReactions(groupId, onReaction) {
  if (!isSupabaseEnabled || !groupId) return null
  const channel = supabase
    .channel(`group-reactions-${groupId}`)
    .on('postgres_changes', {
      event:  'INSERT',
      schema: 'public',
      table:  'message_reactions',
      filter: `group_id=eq.${groupId}`,
    }, payload => onReaction('insert', payload.new))
    .on('postgres_changes', {
      event:  'DELETE',
      schema: 'public',
      table:  'message_reactions',
      filter: `group_id=eq.${groupId}`,
    }, payload => onReaction('delete', payload.old))
    .subscribe()
  return channel
}

export async function loadPicks(groupId, userId, roundKey = 'daily') {
  let meta = null
  try { meta = JSON.parse(localStorage.getItem(`wb_picks_${groupId}_${userId}_${roundKey}`) || 'null') } catch {}

  if (!isSupabaseEnabled || !userId || !groupId) return meta

  try {
    const { data, error } = await supabase
      .from('picks')
      .select('game_id, picked_team, picked_team_name, result, points_earned, round_points, home_team, away_team, game_date')
      .eq('user_id',  userId)
      .eq('group_id', groupId)
    if (error || !data?.length) return meta

    const picks = {}
    for (const row of data) {
      picks[row.game_id] = {
        side:          row.picked_team,
        team:          row.picked_team_name || null,
        status:        row.result        || 'pending',
        pointsAwarded: row.points_earned ?? 0,
        roundPoints:   row.round_points  ?? 1,
        homeTeam:      row.home_team     ?? '',
        awayTeam:      row.away_team     ?? '',
        gameDate:      row.game_date     ?? null,
      }
    }
    return { picks, submitted: meta?.submitted ?? false, dayKey: meta?.dayKey ?? null }
  } catch (err) {
    log('loadPicks error:', err)
    return meta
  }
}

// ── Group finalization ───────────────────────────────────────────────────────
// Thin wrapper around the finalize_group_atomic SECURITY DEFINER RPC
// (migration 013). The RPC is the single source of truth — same function is
// called by the server-side cron path via finalizeGroupServer.
//
// RPC returns:
//   { ok: true,  already_completed, recovered, winnings_inserted, already_paid }
//   { ok: false, reason: 'group missing' | 'archived' | 'pending picks' | 'no members',
//                pending_count? }
//
// Returns null when the call errored or returned ok:false, matching the
// pre-013 return contract.
export async function finalizeGroup(groupId) {
  if (!isSupabaseEnabled || !groupId) return null
  try {
    const { data, error } = await supabase.rpc('finalize_group_atomic', {
      p_group_id: groupId,
    })
    if (error)   { log('finalizeGroup error:', error);            return null }
    if (!data?.ok) { log('finalizeGroup not finalized:', data?.reason); return null }
    return {
      alreadyCompleted: data.already_completed,
      recovered:        data.recovered,
      winningsInserted: data.winnings_inserted,
      alreadyPaid:      data.already_paid,
    }
  } catch (err) {
    log('finalizeGroup error:', err)
    return null
  }
}

// Lazy: if a group's end_date has passed but it's still 'active', finalize it.
// Called from the UI when the user opens a group; cheap no-op otherwise.
// Returns the finalizeGroup result (null if not due) so callers can react —
// e.g. App.jsx reloads the wallet when winnings were just credited.
export async function finalizeIfDue(group) {
  if (!group || !isSupabaseEnabled) return null
  // Allow status='active' (normal path) AND status='completed' (recovery
  // path — a previous broken run may have flipped status without inserting
  // winnings rows). finalizeGroup itself is now idempotent, so re-running
  // on an already-paid group is a cheap series of existence checks.
  if (group.status === 'archived') return null
  if (!group.end_date) return null
  if (new Date(group.end_date).getTime() > Date.now()) return null
  return await finalizeGroup(group.id)
}

// ── User stats (Profile tab) ─────────────────────────────────────────────────
// Aggregates per-user stats across group_members + transactions + profiles in
// one round-trip (3 parallel queries). The leaderboard rollup keeps using its
// own profiles-only query — this is just for "the current user".
export async function getUserStats(userId) {
  if (!isSupabaseEnabled || !userId) return null
  try {
    const [profileRes, membersRes, picksRes] = await Promise.all([
      supabase.from('profiles')
        .select('total_points, created_at')
        .eq('id', userId)
        .single(),
      supabase.from('group_members')
        .select('final_rank, groups (status, end_date)')
        .eq('user_id', userId),
      // Source-of-truth for pick counts. The group_members.correct_picks /
      // total_picks rollups are unreliable here: they get incremented both at
      // submit-time (PickemMode.jsx, BracketMode.jsx) AND at grade-time
      // (server/index.js gradePicksForUser), so they over-count graded picks
      // AND include pending picks. picks.result is authoritative.
      supabase.from('picks')
        .select('result')
        .eq('user_id', userId),
    ])

    const profile = profileRes.data || {}
    const members = membersRes.data || []
    const picks   = picksRes.data   || []

    // Bucket each membership into active vs completed. Archived groups are
    // user-deleted/refunded and don't represent participation, so they're
    // excluded from both counters (and from groupsEntered below).
    let activeGroups    = 0
    let completedGroups = 0
    let groupsWon       = 0
    const todayMs       = Date.now()
    for (const m of members) {
      if (m.final_rank === 1) groupsWon += 1
      const g = m.groups
      if (!g) continue
      if (g.status === 'archived') continue
      const isCompleted = g.status === 'completed'
        || (g.end_date && new Date(g.end_date).getTime() <= todayMs)
      if (isCompleted) completedGroups += 1
      else             activeGroups    += 1
    }

    // Only count picks the grader has actually decided. 'pending' (or NULL
    // legacy rows) is excluded — those are unscored and shouldn't inflate
    // either the denominator or the win-rate calc.
    let correct    = 0
    let totalPicks = 0
    for (const p of picks) {
      if (p.result === 'correct')   { correct += 1; totalPicks += 1 }
      else if (p.result === 'incorrect') {            totalPicks += 1 }
    }

    // Group win %: share of completed groups the user finished 1st in.
    // Active groups are excluded from the denominator — they haven't been
    // decided yet, so counting them would deflate win% just for participating.
    // groupsEntered keeps both buckets for the "Won X/Y groups" display.
    // Distinct from leaderboard `winRate`, which is pick accuracy.
    const groupsEntered = activeGroups + completedGroups
    const groupWinPct   = completedGroups > 0
      ? Math.round((groupsWon / completedGroups) * 100)
      : 0

    return {
      totalPicks,
      correct,
      groupWinPct,
      groupsEntered,
      groupsWon,
      activeGroups,
      memberSince:   profile.created_at || null,
    }
  } catch (err) {
    log('getUserStats error:', err)
    return null
  }
}

// ── Graded picks for the Results view ────────────────────────────────────────
// Reads one user's graded picks (result IN ('correct','incorrect')) inside a
// single group. Used by ResultsFeed for View 1 (current user's own results).
// PR 3 will reuse this with a different userId to power the member-click
// drill-down — which relies on migration 025's cross-member SELECT policy
// (.claude/results-view-snapshot.md §12).
//
// Returns the full pick row including home_team/away_team so the view can
// render the matchup and infer the actual winner per §16 (binary outcome).
// Ordered by game_date desc, created_at desc as tiebreak (game_date is
// text YYYY-MM-DD so the text sort is correct; nulls land last).
export async function loadGradedPicks(groupId, userId) {
  if (!isSupabaseEnabled || !userId || !groupId) return []
  try {
    // Step 1: graded picks. Unchanged from prior behavior.
    const { data, error } = await supabase
      .from('picks')
      .select('id, game_id, picked_team, picked_team_name, result, points_earned, round_points, home_team, away_team, game_date, created_at')
      .eq('group_id', groupId)
      .eq('user_id',  userId)
      .in('result',   ['correct', 'incorrect'])
      .order('game_date',  { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
    if (error) { log('loadGradedPicks error:', error); return [] }
    const picks = data || []
    if (!picks.length) return picks

    // Step 2: optional outcome enrichment from game_results, keyed by
    // game_id. Separate query (not a PostgREST embed) because picks has no
    // FK to game_results — legacy picks predate the table and have
    // game_ids that will never appear there. Result is attached as
    // p.game_results on each row; null when the grader hasn't persisted
    // an outcome row for that game yet (or migration 028 hasn't been
    // applied). Renderer treats null as "no outcome data, fall through to
    // matchup-only layout" — preserves pre-Preview-1 behavior exactly.
    //
    // Failure here MUST NOT remove rows. If the game_results SELECT errors
    // (table missing, RLS misconfig, transient network) we still return
    // the picks. The Results UI must keep working even if outcome
    // enrichment is unavailable — same fail-open posture as the writer
    // side of the pipeline.
    try {
      const gameIds = [...new Set(picks.map(p => p.game_id).filter(Boolean))]
      if (gameIds.length === 0) return picks
      const { data: results, error: rErr } = await supabase
        .from('game_results')
        .select('game_id, home_score, away_score, status, sport, details')
        .in('game_id', gameIds)
      if (rErr) { log('loadGradedPicks game_results error:', rErr); return picks }
      const byId = new Map((results || []).map(r => [r.game_id, r]))
      return picks.map(p => ({ ...p, game_results: byId.get(p.game_id) || null }))
    } catch (err) {
      log('loadGradedPicks game_results enrichment failed:', err)
      return picks
    }
  } catch (err) {
    log('loadGradedPicks error:', err)
    return []
  }
}

// ── Recent activity ──────────────────────────────────────────────────────────
// Returns the user's most recently resolved picks across all groups.
// Used by the Profile tab's activity feed.
export async function getRecentPicks(userId, limit = 5) {
  if (!isSupabaseEnabled || !userId) return []
  try {
    const { data, error } = await supabase
      .from('picks')
      .select('id, group_id, game_id, picked_team, picked_team_name, result, points_earned, created_at')
      .eq('user_id', userId)
      .in('result', ['correct', 'incorrect'])
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) { log('getRecentPicks error:', error); return [] }
    return data || []
  } catch (err) {
    log('getRecentPicks error:', err)
    return []
  }
}

// ── Pick grading (Pick'em mode) ──────────────────────────────────────────────
// Looks up pending picks in Supabase, matches them against the games array
// (already-fetched ESPN data) and writes correct/incorrect + points back.
// Member row gets credited the delta. Idempotent: only touches pending rows,
// so re-running on a fully-graded group is a no-op.
//
// `games` shape matches getGames(): { id, status, home: { team, score },
// away: { team, score }, roundPts? }.
// ── Pick grading (server-side) ───────────────────────────────────────────────
// Grading now runs on the Railway server which has a 120-day ESPN window
// and direct Supabase access via the service-role key. This function is a
// thin wrapper that POSTs to /api/grade-picks. The server reads the user's
// pending picks, matches them against the wide game set, writes back
// result + points_earned on each pick, and credits group_members.
//
// Returns whatever the server returns (typically { graded, correct,
// totalPoints, gamesSearched }) or null on error.
export async function resolvePicks(groupId, userId) {
  if (!groupId || !userId) return null
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
  try {
    const res = await fetch(`${API_URL}/api/grade-picks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groupId, userId }),
    })
    const result = await res.json()
    log('[resolvePicks] server result:', result)
    return result
  } catch (err) {
    log('[resolvePicks] failed:', err?.message || err)
    return null
  }
}

// ── Bracket picks ─────────────────────────────────────────────────────────────
// bracket_picks columns: id, user_id, group_id, picks, submitted, points, created_at

export async function saveBracket(groupId, userId, bracketData) {
  if (!isSupabaseEnabled || !userId || !groupId) {
    try { localStorage.setItem(`wb_bracket_${groupId}_${userId}`, JSON.stringify(bracketData)) }
    catch (_) {}
    return { data: null, error: null }
  }
  try {
    // Pass-through for v2 payloads — `bracketData.picks` is already
    // { v: 2, picks: [{round, slot, team}, ...], lengths?, migration_warnings? }
    // assembled by BracketMode.jsx. Stored as JSONB unchanged.
    //
    // Legacy v1 fallback retained for any caller still passing a flat
    // {matchupId: team} map. Filters placeholder-keyed picks (slots whose
    // teams shift as ESPN publishes new series). v2 picks key on stable
    // {round, slot} addresses and never include placeholders, so this path
    // shouldn't fire post-Commit-B; left in place for safety until the v1
    // payload shape is fully retired across all callers.
    let picksPayload
    if (bracketData.picks?.v === 2) {
      picksPayload = bracketData.picks
    } else {
      const rawPicks      = bracketData.picks || {}
      const filteredPicks = {}
      for (const [id, pick] of Object.entries(rawPicks)) {
        if (id.includes('placeholder')) continue
        filteredPicks[id] = pick
      }
      if (bracketData.lengths && Object.keys(bracketData.lengths).length) {
        filteredPicks.__lengths = bracketData.lengths
      }
      const droppedKeys = Object.keys(rawPicks).filter(id => !(id in filteredPicks))
      if (droppedKeys.length) {
        console.warn('[bracket] save: dropped placeholder pick IDs (v1 path)', droppedKeys)
      }
      picksPayload = filteredPicks
    }

    // submitted and points are optional. When caller omits them, leave the
    // existing DB value untouched — the cleanup write-back uses this to
    // update picks without changing the submitted flag as a side effect.
    const row = { group_id: groupId, user_id: userId, picks: picksPayload }
    if (bracketData.submitted !== undefined) row.submitted = bracketData.submitted
    if (bracketData.points    !== undefined) row.points    = bracketData.points

    const { data, error } = await supabase
      .from('bracket_picks')
      .upsert(row, { onConflict: 'group_id,user_id' })
    return { data, error }
  } catch (err) {
    console.warn('[bracket] save: exception', err?.message || err)
    return { data: null, error: err }
  }
}

export async function loadBracket(groupId, userId) {
  if (!isSupabaseEnabled || !userId || !groupId) {
    try { return JSON.parse(localStorage.getItem(`wb_bracket_${groupId}_${userId}`) || 'null') } catch { return null }
  }
  try {
    const { data, error } = await supabase
      .from('bracket_picks')
      .select('picks, submitted, points')
      .eq('group_id', groupId)
      .eq('user_id',  userId)
      .maybeSingle()
    if (error) { log('loadBracket error:', error); return null }
    if (!data) return null
    let lengths = {}
    if (data.picks && data.picks.__lengths) {
      lengths = data.picks.__lengths
      const { __lengths, ...rest } = data.picks
      data.picks = rest
    }
    return { ...data, lengths }
  } catch (err) {
    log('loadBracket failed:', err)
    return null
  }
}

// ── Bracket grading ───────────────────────────────────────────────────────────
// Recomputes total bracket points from current ESPN winners + saved picks,
// then credits the *delta* relative to bracket_picks.points so it stays
// idempotent across re-runs (e.g. the 5-minute auto-refresh in BracketMode).
//
// Current bracket shape (from fetchPlayoffBracket): { rounds: [...], finals, ... }
// Each round has { name, points, matchups: [{ id, team1, team2, winner, ... }] }.
// Older shape was { east:{r1,r2,cf}, west:{r1,r2,cf}, finals } — kept the
// hardcoded points map as a final fallback for unknown shapes.
const BRACKET_ROUND_PTS = { r1: 1, r2: 2, cf: 4, fin: 8 }

export async function gradeBracketPicks(groupId, userId, bracket) {
  if (!bracket?.rounds?.length) return 0
  const saved = await loadBracket(groupId, userId)
  if (!saved?.picks) return 0

  // Walk every round, counting correct picks + graded matchups + points.
  // Source of round point values: bracket.rounds[i].points (set during
  // buildBracketFromGames). Fallback to the hardcoded map keyed on the
  // local r1/r2/cf/fin slot so an unexpected shape still scores something.
  let totalPoints = 0
  let correct     = 0
  let graded      = 0
  const slotId = ['r1', 'r2', 'cf', 'fin']
  // Build a lookup keyed by ${roundAbbr}:${slot} from saved.picks.
  // v2 shape: { v: 2, picks: [{round, slot, team}, ...] }
  // v1 shape (legacy): { matchupId: team, ... } — preserved as fallback.
  const picksLookup = new Map()
  if (saved.picks?.v === 2 && Array.isArray(saved.picks.picks)) {
    saved.picks.picks.forEach(p => {
      if (!p?.round || typeof p.slot !== 'number' || !p.team) return
      picksLookup.set(`${p.round}:${p.slot}`, p.team)
    })
  }
  // v1 fallback: also index by matchup ID for legacy rows that haven't
  // been migrated yet (submitted brackets stay v1 in DB by design).
  const v1Picks = (saved.picks?.v === 2) ? null : saved.picks

  bracket.rounds.forEach((round, idx) => {
    const pts = round.points ?? BRACKET_ROUND_PTS[slotId[idx]] ?? 1
    for (const m of (round.matchups || [])) {
      if (!m.winner) continue
      // v2 lookup by {round, slot}; v1 fallback by matchup ID.
      const picked = picksLookup.get(`${m.roundAbbr}:${m.slot}`)
        ?? (v1Picks ? v1Picks[m.id] : undefined)
      // Only count graded matchups the user actually picked. The
      // total_picks column on group_members means "picks you made that
      // got graded" — not "matchups in this bracket that got graded."
      // GroupCard reads total_picks for its "X Picks Made" badge; if we
      // count every graded matchup, late-joiners and incomplete brackets
      // show inflated picks (e.g. "12 Picks Made" when the user picked 4).
      if (!picked) continue
      graded += 1
      if (picked === m.winner) {
        totalPoints += pts
        correct     += 1
      }
    }
  })

  // gradeBracketPicks computes absolute values each run, but updateMemberPoints
  // is delta-additive (matching the server's gradePicksForUser pattern). So
  // diff against whatever's currently in group_members to get the delta to
  // write. bracket-only groups have a single writer (this function), so the
  // read-modify-write race is theoretical, not practical.
  let prevCorrect = 0
  let prevTotal   = 0
  if (isSupabaseEnabled) {
    try {
      const { data: gm } = await supabase
        .from('group_members')
        .select('correct_picks, total_picks')
        .eq('group_id', groupId)
        .eq('user_id', userId)
        .single()
      prevCorrect = gm?.correct_picks || 0
      prevTotal   = gm?.total_picks   || 0
    } catch (e) { log('gradeBracketPicks gm read:', e?.message) }
  }

  const pointsDelta  = totalPoints - (saved.points || 0)
  const correctDelta = correct - prevCorrect
  const totalDelta   = graded  - prevTotal

  if (pointsDelta === 0 && correctDelta === 0 && totalDelta === 0) return totalPoints

  await updateMemberPoints(groupId, userId, pointsDelta, correctDelta, totalDelta)

  // Persist the new total so the next run's delta is computed against it.
  if (isSupabaseEnabled) {
    try {
      const { error } = await supabase
        .from('bracket_picks')
        .update({ points: totalPoints })
        .eq('group_id', groupId)
        .eq('user_id', userId)
      if (error) log('gradeBracketPicks update error:', error)
    } catch (err) {
      log('gradeBracketPicks failed:', err)
    }
  } else {
    try {
      const key      = `wb_bracket_${groupId}_${userId}`
      const existing = JSON.parse(localStorage.getItem(key) || '{}')
      localStorage.setItem(key, JSON.stringify({ ...existing, points: totalPoints }))
    } catch {}
  }
  return totalPoints
}

// ── Transactions ──────────────────────────────────────────────────────────────
// transactions columns: id, user_id, amount, type, description, created_at

export async function addTransaction(userId, amount, type, description = '', groupId = null) {
  if (isSupabaseEnabled) {
    try {
      const { error } = await supabase.from('transactions').insert({
        user_id:     userId,
        amount:      amount,
        type:        type,
        description: description,
        group_id:    groupId,
      })
      if (error) log('addTransaction error:', error)
    } catch (err) {
      log('addTransaction error:', err)
    }
    return
  }
}

// ── Leaderboard ───────────────────────────────────────────────────────────────
// profiles columns: id, username, total_points, created_at, total_won

export async function getLeaderboard(currentUserId) {
  if (isSupabaseEnabled) {
    try {
      const [allTimeRes, thisMonthRes] = await Promise.all([
        supabase.rpc('get_leaderboard_all_time',  { p_current_user: currentUserId }),
        supabase.rpc('get_leaderboard_this_month', { p_current_user: currentUserId }),
      ])
      if (allTimeRes.error)   log('getLeaderboard all_time error:',   allTimeRes.error)
      if (thisMonthRes.error) log('getLeaderboard this_month error:', thisMonthRes.error)
      // RPC returns snake_case columns (Postgres convention); App.jsx
      // consumes camelCase (legacy JS aggregation shape). Remap here so the
      // RPC stays Postgres-idiomatic and the React renderer is unchanged.
      const remapRow = (r) => ({
        user_id:       r.user_id,
        username:      r.username,
        avatar_url:    r.avatar_url,
        totalWon:      Number(r.total_won)     || 0,
        totalWagered:  Number(r.total_wagered) || 0,
        groupsEntered: r.groups_entered || 0,
        groupsWon:     r.groups_won     || 0,
        activeGroups:  r.active_groups  || 0,
        correct:       r.correct        || 0,
        totalPicks:    r.total_picks    || 0,
        winRate:       r.win_rate       || 0,
        isMe:          !!r.is_me,
      })
      return {
        allTime:   (allTimeRes.data   || []).map(remapRow),
        thisMonth: (thisMonthRes.data || []).map(remapRow),
      }
    } catch (err) {
      log('getLeaderboard error:', err)
      return { allTime: [], thisMonth: [] }
    }
  }
  const me = read(KEYS.profile)
  const allTime = [
    { username:'KingJay',     totalWon:3200, totalWagered:4250, groupsWon:8, groupsEntered:14, winRate:57.1, activeGroups:3, correct:87, totalPicks:112 },
    { username:'BracketBoss', totalWon:2850, totalWagered:3800, groupsWon:7, groupsEntered:12, winRate:58.3, activeGroups:2, correct:81, totalPicks:105 },
    { username:'Showtime23',  totalWon:2200, totalWagered:3000, groupsWon:5, groupsEntered:10, winRate:50.0, activeGroups:2, correct:74, totalPicks:98  },
    { username:'Nugz4Life',   totalWon:1800, totalWagered:2500, groupsWon:4, groupsEntered:9,  winRate:44.4, activeGroups:1, correct:69, totalPicks:94  },
    { username:'ThunderBolt', totalWon:1450, totalWagered:2100, groupsWon:4, groupsEntered:8,  winRate:50.0, activeGroups:1, correct:58, totalPicks:84  },
    { username:'CelticsPride',totalWon:1100, totalWagered:1800, groupsWon:3, groupsEntered:8,  winRate:37.5, activeGroups:2, correct:50, totalPicks:76  },
    { username:'NightOwl',    totalWon: 900, totalWagered:1600, groupsWon:3, groupsEntered:7,  winRate:42.9, activeGroups:1, correct:44, totalPicks:68  },
    { username:'YankeesFan',  totalWon: 750, totalWagered:1400, groupsWon:2, groupsEntered:6,  winRate:33.3, activeGroups:1, correct:40, totalPicks:63  },
    { username:'FinalFour',   totalWon: 620, totalWagered:1200, groupsWon:2, groupsEntered:6,  winRate:33.3, activeGroups:1, correct:34, totalPicks:55  },
    { username:'DodgerBlue',  totalWon: 480, totalWagered: 950, groupsWon:2, groupsEntered:5,  winRate:40.0, activeGroups:1, correct:28, totalPicks:47  },
    { username:'HeatNation',  totalWon: 320, totalWagered: 750, groupsWon:1, groupsEntered:5,  winRate:20.0, activeGroups:1, correct:24, totalPicks:42  },
    { username:'SeriesKing',  totalWon: 200, totalWagered: 600, groupsWon:1, groupsEntered:4,  winRate:25.0, activeGroups:0, correct:20, totalPicks:36  },
  ]
  const thisMonth = [
    { username:'BracketBoss', totalWon:480, totalWagered:600, groupsWon:2, groupsEntered:3, winRate:66.7, activeGroups:2, correct:24, totalPicks:28 },
    { username:'Nugz4Life',   totalWon:350, totalWagered:500, groupsWon:1, groupsEntered:3, winRate:33.3, activeGroups:1, correct:18, totalPicks:22 },
    { username:'KingJay',     totalWon:300, totalWagered:450, groupsWon:1, groupsEntered:3, winRate:33.3, activeGroups:3, correct:22, totalPicks:28 },
    { username:'ThunderBolt', totalWon:250, totalWagered:350, groupsWon:1, groupsEntered:2, winRate:50.0, activeGroups:1, correct:16, totalPicks:21 },
    { username:'Showtime23',  totalWon:200, totalWagered:400, groupsWon:1, groupsEntered:2, winRate:50.0, activeGroups:2, correct:18, totalPicks:24 },
    { username:'CelticsPride',totalWon:150, totalWagered:300, groupsWon:1, groupsEntered:2, winRate:50.0, activeGroups:2, correct:14, totalPicks:19 },
    { username:'DodgerBlue',  totalWon:120, totalWagered:250, groupsWon:1, groupsEntered:2, winRate:50.0, activeGroups:1, correct:12, totalPicks:17 },
    { username:'NightOwl',    totalWon: 90, totalWagered:200, groupsWon:0, groupsEntered:2, winRate: 0.0, activeGroups:1, correct:10, totalPicks:15 },
    { username:'YankeesFan',  totalWon: 75, totalWagered:200, groupsWon:0, groupsEntered:2, winRate: 0.0, activeGroups:1, correct: 9, totalPicks:14 },
    { username:'HeatNation',  totalWon:  0, totalWagered:150, groupsWon:0, groupsEntered:1, winRate: 0.0, activeGroups:1, correct: 7, totalPicks:12 },
  ]
  if (me) {
    allTime.splice(7, 0, { username:me.username||'You', totalWon:820, totalWagered:1350, groupsWon:2, groupsEntered:6, winRate:33.3, activeGroups:3, correct:37, totalPicks:58, isMe:true })
    thisMonth.splice(6, 0, { username:me.username||'You', totalWon:140, totalWagered:200, groupsWon:1, groupsEntered:2, winRate:50.0, activeGroups:3, correct:11, totalPicks:14, isMe:true })
  }
  return { allTime, thisMonth }
}

// ── Demo group seed (localStorage only) ──────────────────────────────────────

function seedGroups() {
  if (isSupabaseEnabled) return
  if (read(KEYS.groups)) return
  write(KEYS.groups, [
    { id:'g001', name:"NBA Playoff Pick'em",      contest_type:'pick_em', duration_type:'daily',    sport:'NBA', entry_fee:25, prize_split:'top_three',     created_by:'demo', invite_code:'NBA2026', status:'active', prize_pot:175, created_at:now() },
    { id:'g002', name:'Office Bracket Challenge', contest_type:'bracket', duration_type:'playoffs', sport:'NBA', entry_fee:50, prize_split:'top_two',        created_by:'demo', invite_code:'BRCKT1',  status:'active', prize_pot:450, created_at:now() },
    { id:'g003', name:"NHL Playoff Pick'em",      contest_type:'pick_em', duration_type:'daily',    sport:'NHL', entry_fee:20, prize_split:'top_two',        created_by:'demo', invite_code:'NHLPCK',  status:'active', prize_pot:120, created_at:now() },
    { id:'g004', name:"MLB Season Pick'em",       contest_type:'pick_em', duration_type:'daily',    sport:'MLB', entry_fee:10, prize_split:'winner_all',     created_by:'demo', invite_code:'MLBPCK',  status:'active', prize_pot:80,  created_at:now() },
    { id:'g005', name:'NHL Bracket',              contest_type:'bracket', duration_type:'playoffs', sport:'NHL', entry_fee:30, prize_split:'flat_top_three', created_by:'demo', invite_code:'NHLBKT',  status:'active', prize_pot:150, created_at:now() },
    { id:'g006', name:"NBA Full Playoff Pick'em", contest_type:'pick_em', duration_type:'playoffs', sport:'NBA', entry_fee:20, prize_split:'second_back',    created_by:'demo', invite_code:'PLYPCK',  status:'active', prize_pot:100, created_at:now() },
  ])
  write(KEYS.members, [
    {id:'m001',group_id:'g001',user_id:'demo',username:'You',         points:9, correct:9, total:15,joined_at:now()},
    {id:'m002',group_id:'g001',user_id:'u001',username:'KingJay',     points:12,correct:12,total:15,joined_at:now()},
    {id:'m003',group_id:'g001',user_id:'u002',username:'Showtime23',  points:10,correct:10,total:15,joined_at:now()},
    {id:'m004',group_id:'g001',user_id:'u003',username:'Nugz4Life',   points:8, correct:8, total:15,joined_at:now()},
    {id:'m005',group_id:'g001',user_id:'u004',username:'CelticsPride',points:7, correct:7, total:15,joined_at:now()},
    {id:'m006',group_id:'g001',user_id:'u005',username:'HeatNation',  points:5, correct:5, total:15,joined_at:now()},
    {id:'m007',group_id:'g001',user_id:'u006',username:'ThunderBolt', points:4, correct:4, total:15,joined_at:now()},
    {id:'m008',group_id:'g002',user_id:'demo',username:'You',         points:24,correct:24,total:30,joined_at:now()},
    {id:'m009',group_id:'g002',user_id:'u001',username:'KingJay',     points:30,correct:30,total:30,joined_at:now()},
    {id:'m010',group_id:'g002',user_id:'u002',username:'Showtime23',  points:18,correct:18,total:30,joined_at:now()},
    {id:'m011',group_id:'g002',user_id:'u007',username:'BracketBoss', points:28,correct:28,total:30,joined_at:now()},
    {id:'m012',group_id:'g002',user_id:'u008',username:'FinalFour',   points:22,correct:22,total:30,joined_at:now()},
    {id:'m013',group_id:'g003',user_id:'demo',username:'You',         points:6, correct:6, total:10,joined_at:now()},
    {id:'m014',group_id:'g003',user_id:'u001',username:'KingJay',     points:7, correct:7, total:10,joined_at:now()},
    {id:'m015',group_id:'g003',user_id:'u003',username:'Nugz4Life',   points:9, correct:9, total:10,joined_at:now()},
    {id:'m016',group_id:'g004',user_id:'demo',username:'You',         points:9, correct:9, total:14,joined_at:now()},
    {id:'m017',group_id:'g004',user_id:'u010',username:'YankeesFan',  points:11,correct:11,total:14,joined_at:now()},
    {id:'m018',group_id:'g004',user_id:'u011',username:'DodgerBlue',  points:8, correct:8, total:14,joined_at:now()},
    {id:'m019',group_id:'g005',user_id:'demo',username:'You',         points:16,correct:16,total:22,joined_at:now()},
    {id:'m020',group_id:'g005',user_id:'u001',username:'KingJay',     points:20,correct:20,total:22,joined_at:now()},
    {id:'m021',group_id:'g005',user_id:'u003',username:'Nugz4Life',   points:12,correct:12,total:22,joined_at:now()},
    {id:'m022',group_id:'g006',user_id:'demo',username:'You',         points:0, correct:0, total:0, joined_at:now()},
    {id:'m023',group_id:'g006',user_id:'u001',username:'KingJay',     points:2, correct:2, total:5, joined_at:now()},
    {id:'m024',group_id:'g006',user_id:'u002',username:'Showtime23',  points:1, correct:1, total:5, joined_at:now()},
    {id:'m025',group_id:'g006',user_id:'u007',username:'BracketBoss', points:3, correct:3, total:5, joined_at:now()},
  ])
}
